/*
  ============================================================
  CHARISMA COSMETICS — REVIEWS SECTION v6
  File: assets/charisma-reviews.js

  Powers the .cr-carousel slider:
   • Reads --cr-cols from the section (set by CSS breakpoints)
     so cards-per-view stays in sync with the stylesheet
   • Arrow + dot navigation, loop or clamp at the ends
   • Drag / touch swipe
   • Autoplay (pauses on hover, respects prefers-reduced-motion)
   • Reveals card entrance animation when the section scrolls
     into view
   • Re-inits automatically on theme-editor section reloads
  ============================================================
*/
(function () {
  'use strict';

  var initializedCarousels = new WeakSet();

  function initCarousel(carousel) {
    if (initializedCarousels.has(carousel)) return;
    initializedCarousels.add(carousel);

    var viewport = carousel.querySelector('.cr-viewport');
    var track = carousel.querySelector('.cr-track');
    if (!viewport || !track) return;

    var cards = Array.prototype.slice.call(track.children);
    if (!cards.length) return;

    var section = carousel.closest('.cr-section');
    var prevBtn = carousel.querySelector('.cr-nav--prev');
    var nextBtn = carousel.querySelector('.cr-nav--next');
    var dotsWrap = carousel.querySelector('.cr-dots');

    var autoplay = carousel.getAttribute('data-autoplay') === 'true';
    var autoplaySpeed = parseInt(carousel.getAttribute('data-autoplay-speed'), 10) || 5000;
    var loop = carousel.getAttribute('data-loop') === 'true';
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var currentIndex = 0;
    var cardsPerView = 1;
    var maxIndex = 0;
    var autoplayTimer = null;
    var isDragging = false;
    var dragStartX = 0;
    var dragDelta = 0;
    var baseTranslate = 0;

    function getCardsPerView() {
      if (!section) return 1;
      var raw = getComputedStyle(section).getPropertyValue('--cr-cols');
      var n = parseInt(raw, 10);
      return n > 0 ? n : 1;
    }

    function getGap() {
      var style = getComputedStyle(track);
      var gap = style.columnGap || style.gap;
      return parseFloat(gap) || 0;
    }

    function getStep() {
      var rect = cards[0].getBoundingClientRect();
      return rect.width + getGap();
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var pageCount = Math.max(1, Math.ceil(cards.length / cardsPerView));
      if (pageCount <= 1) {
        dotsWrap.style.display = 'none';
        return;
      }
      dotsWrap.style.display = '';
      for (var i = 0; i < pageCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cr-dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to review page ' + (i + 1));
        (function (pageIndex) {
          dot.addEventListener('click', function () {
            goTo(pageIndex * cardsPerView);
            restartAutoplay();
          });
        })(i);
        dotsWrap.appendChild(dot);
      }
      updateDots();
    }

    function updateDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('.cr-dot');
      var activePage = Math.round(currentIndex / cardsPerView);
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('cr-dot--active', i === activePage);
      }
    }

    function updateArrows() {
      var hasOverflow = cards.length > cardsPerView;
      if (prevBtn) prevBtn.style.display = hasOverflow ? '' : 'none';
      if (nextBtn) nextBtn.style.display = hasOverflow ? '' : 'none';
      if (loop || !hasOverflow) return;
      if (prevBtn) prevBtn.disabled = currentIndex <= 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= maxIndex;
    }

    function render(animate) {
      var step = getStep();
      if (animate === false) track.style.transition = 'none';
      track.style.transform = 'translateX(' + (-currentIndex * step) + 'px)';
      if (animate === false) {
        // force reflow then restore transition so the next move animates
        track.getBoundingClientRect();
        track.style.transition = '';
      }
      updateDots();
      updateArrows();
    }

    function clampIndex(index) {
      if (loop) {
        if (index < 0) return maxIndex;
        if (index > maxIndex) return 0;
        return index;
      }
      return Math.max(0, Math.min(maxIndex, index));
    }

    function goTo(index) {
      currentIndex = clampIndex(index);
      render();
    }

    function next() { goTo(currentIndex + 1); }
    function prev() { goTo(currentIndex - 1); }

    function recalc() {
      cardsPerView = getCardsPerView();
      maxIndex = Math.max(0, cards.length - cardsPerView);
      if (currentIndex > maxIndex) currentIndex = maxIndex;
      buildDots();
      render(false);
    }

    function startAutoplay() {
      if (!autoplay || reducedMotion || cards.length <= cardsPerView) return;
      stopAutoplay();
      autoplayTimer = setInterval(next, autoplaySpeed);
    }
    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }
    function restartAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        prev();
        restartAutoplay();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        next();
        restartAutoplay();
      });
    }

    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);

    /* ── Drag / swipe ── */
    function pointerX(e) {
      return e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
    }
    function onPointerDown(e) {
      if (cards.length <= cardsPerView) return;
      isDragging = true;
      dragStartX = pointerX(e);
      dragDelta = 0;
      baseTranslate = -currentIndex * getStep();
      track.classList.add('cr-track--dragging');
      stopAutoplay();
    }
    function onPointerMove(e) {
      if (!isDragging) return;
      dragDelta = pointerX(e) - dragStartX;
      track.style.transform = 'translateX(' + (baseTranslate + dragDelta) + 'px)';
    }
    function onPointerUp() {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('cr-track--dragging');
      var step = getStep();
      var threshold = step * 0.18;
      if (dragDelta < -threshold) next();
      else if (dragDelta > threshold) prev();
      else render();
      restartAutoplay();
    }

    track.addEventListener('mousedown', onPointerDown);
    track.addEventListener('touchstart', onPointerDown, { passive: true });
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('touchmove', onPointerMove, { passive: true });
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchend', onPointerUp);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    /* ── Resize ── */
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recalc, 150);
    });

    /* ── Entrance animation reveal ── */
    if (section) {
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              cards.forEach(function (c) { c.classList.add('cr-card--visible'); });
              obs.disconnect();
            }
          });
        }, { threshold: 0.15 });
        observer.observe(section);
      } else {
        cards.forEach(function (c) { c.classList.add('cr-card--visible'); });
      }
    }

    recalc();
    startAutoplay();
  }

  function initAll(root) {
    var scope = root || document;
    var carousels = scope.querySelectorAll('.cr-carousel');
    carousels.forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  /* Re-init when a merchant adds/reloads this section in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();